import { useMemo, ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CashbackIcon,
  ReceiptDollarIcon,
  ViewIcon,
  SentIcon,
  UserCircleIcon,
  CancelCircleIcon,
  AttachmentIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { ActionLabel } from "./ActionLabel";
import { ApplicationStatus, Invitee } from "shared/types";
import { Applicant } from "types/application";
import {
  Action,
  ContextMenuPopover
} from "shared/components/ContextMenuPopover";
import { ApplicationActionsMenuVariant } from "./types";
import { ApplicantActions, isApplicantEntity } from "./ApplicantActions";

export const ApplicationActionsMenu = ({
  variant,
  disabled,
  triggerClassName,
  contentClassName,
  borderLess,
  trigger,
  cbTrigger,
  applicant,
  pendingMagicLink,
  applicationStatus,
  allowCoSigner,
  allowOccupant,
  onCancelClick,
  onUncancelClick,
  onChangeRoleClick,
  onSetPrimaryClick,
  onRefundClick,
  onResendInviteClick,
  hasAdminPrivileges,
  permissions
}: {
  variant: ApplicationActionsMenuVariant;
  disabled?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
  borderLess?: boolean;
  trigger?: ReactElement;
  cbTrigger?: (open: boolean) => ReactElement;

  applicant: Applicant | Invitee;
  pendingMagicLink?: Invitee;
  applicationStatus?: ApplicationStatus;
  allowCoSigner: boolean;
  allowOccupant: boolean;

  onCancelClick?: (applicantId: string) => void;
  onUncancelClick?: (applicantId: string) => void;
  onChangeRoleClick?: (applicant: Applicant | Invitee) => void;
  onSetPrimaryClick?: (applicant: Applicant | Invitee) => void;
  onRefundClick?: (applicantId: string) => void;
  onResendInviteClick?: (invite: Invitee) => void;

  hasAdminPrivileges: boolean;
  permissions: {
    canChangeApplicantRole?: boolean;
    canCancelApplications?: boolean;
  };
}) => {
  const navigate = useNavigate();

  const actions = useMemo<Action[]>(() => {
    const isApplicant = isApplicantEntity(applicant);
    const applicantId = isApplicant
      ? String(applicant.id)
      : String(pendingMagicLink?.id);
    const hasApplication = Boolean(applicant.application_id);

    const actionStates = new ApplicantActions({
      variant,
      applicant,
      pendingMagicLink,
      applicationStatus,
      allowCoSigner,
      allowOccupant,
      hasAdminPrivileges,
      permissions
    });

    const cancelOrUncancel = actionStates.cancelOrUncancel;

    const result: Action[] = [];

    if (!actionStates.review.hidden)
      result.push({
        title: <ActionLabel icon={ViewIcon} label="Review" />,
        disabled: actionStates.review.disabled,
        tooltip: actionStates.review.tooltip,
        onClick: () => {
          if (!hasApplication) {
            toast.warning("Applicant received an invite, but didn't apply yet");
            return;
          }
          if (isApplicant)
            navigate(
              `/applications/${applicant.application_id}/reports?applicantId=${applicant.id}`
            );
        }
      });

    if (!actionStates.resendInvite.hidden)
      result.push({
        title: <ActionLabel icon={SentIcon} label="Resend invite" />,
        onClick: () =>
          pendingMagicLink && onResendInviteClick?.(pendingMagicLink)
      });

    if (!actionStates.changeRole.hidden)
      result.push({
        title: <ActionLabel icon={UserCircleIcon} label="Change role" />,
        disabled: actionStates.changeRole.disabled,
        tooltip: actionStates.changeRole.tooltip,
        onClick: () => isApplicant && onChangeRoleClick?.(applicant)
      });

    if (onSetPrimaryClick && !actionStates.setPrimaryApplicant.hidden)
      result.push({
        title: (
          <ActionLabel icon={UserCircleIcon} label="Set as primary applicant" />
        ),
        disabled: actionStates.setPrimaryApplicant.disabled,
        tooltip: actionStates.setPrimaryApplicant.tooltip,
        onClick: () => isApplicant && onSetPrimaryClick(applicant)
      });

    result.push(
      cancelOrUncancel.showUncancel
        ? {
            title: (
              <ActionLabel
                icon={CancelCircleIcon}
                label="Uncancel application"
              />
            ),
            disabled: cancelOrUncancel.disabled,
            tooltip: cancelOrUncancel.tooltip,
            onClick: () => onUncancelClick?.(applicantId)
          }
        : {
            title: (
              <ActionLabel icon={CancelCircleIcon} label="Cancel application" />
            ),
            disabled: cancelOrUncancel.disabled,
            tooltip: cancelOrUncancel.tooltip,
            onClick: () =>
              !cancelOrUncancel.disabled && onCancelClick?.(applicantId)
          }
    );

    if (!actionStates.printReceipt.hidden)
      result.push({
        title: <ActionLabel icon={ReceiptDollarIcon} label="Print receipt" />,
        disabled: actionStates.printReceipt.disabled,
        tooltip: actionStates.printReceipt.tooltip,
        onClick: () => {
          if (!isApplicant) toast.error("Unable to print receipt");
          else
            window.open(
              `/applications/${applicant.application_id}/reports/print/receipt?applicantId=${applicant.id}`,
              "_blank"
            );
        }
      });

    if (!actionStates.refund.hidden)
      result.push({
        title: <ActionLabel icon={CashbackIcon} label="Refund application" />,
        disabled: actionStates.refund.disabled,
        tooltip: actionStates.refund.tooltip,
        onClick: () => isApplicant && onRefundClick?.(applicantId)
      });

    if (!actionStates.uploadDocuments.hidden)
      result.push({
        title: <ActionLabel icon={AttachmentIcon} label="Upload documents" />,
        disabled: actionStates.uploadDocuments.disabled,
        tooltip: actionStates.uploadDocuments.tooltip,
        onClick: () => {
          if (!hasApplication) {
            toast.warning("Applicant received an invite, but didn't apply yet");
            return;
          }
          if (isApplicant)
            navigate(
              `/applications/${applicant.application_id}/reports?applicantId=${applicant.id}&report=additional_documents`
            );
        }
      });

    return result;
  }, [
    variant,
    applicant,
    pendingMagicLink,
    applicationStatus,
    allowCoSigner,
    allowOccupant,
    hasAdminPrivileges,
    permissions.canChangeApplicantRole,
    permissions.canCancelApplications,
    navigate,
    onCancelClick,
    onUncancelClick,
    onChangeRoleClick,
    onSetPrimaryClick,
    onRefundClick,
    onResendInviteClick
  ]);

  return (
    <ContextMenuPopover
      actions={actions}
      disabled={disabled}
      placement="bottom-end"
      triggerClassName={triggerClassName}
      contentClassName={contentClassName}
      borderLess={borderLess}
      trigger={trigger}
      cbTrigger={cbTrigger}
    />
  );
};
